import { spreadWheatType } from '@/modules/common/helpers/wheat-type.helper';
import { $mixedBags } from '@entities/simple-mix/model';
import { SLEEVE_WEIGHT_TONS_PER_METER } from '../constants/sleeve.constants';

export class SleeveManager<T extends number[][][] | SleeveDescription[]> {
  private _pathH: any;
  private _pathW: any;
  private _top: number[] = [0, 0];
  private _bottom: number[] = [0, 0];
  private _map: ymaps.Map;
  private _projection: object;
  private _angleLocal: number = 0;
  private _sleeves: Sleeve[] = [];
  private _sleevesMap: SleevesMap = {};

  /**
   * Длина рукава в метрах
   */
  private _sleeveLength: number = 60;

  public get sleeveLength(): number {
    return this._sleeveLength;
  }

  public set sleeveLength(v: number) {
    this._sleeveLength = v;
  }

  /**
   * Ширина рукава в метрах
   */
  private _sleeveWidth: number = 3.5;

  public get sleeveWidth(): number {
    return this._sleeveWidth;
  }

  public set sleeveWidth(v: number) {
    this._sleeveWidth = v;
  }

  /**
   * ctor
   */
  constructor(mapRef: ymaps.Map, options?: Options) {
    this._map = mapRef;
    this._projection = mapRef.options.get('projection', {});
  }

  public dispose() {
    if (this._map) {
      this._map.events.remove('boundschange', this.onBoundsChange);
      this._sleeves = [];
      this._sleevesMap = {};
    }
  }

  /**
   * @param sleeves - массив с географическими координатами точек начала/конца рукавов
   * @example const sleeves = [[[45.05415359546938, 38.97059663223253], [45.05405359546938, 38.97129663223253]]]
   */
  public buildSleevesByStartEndPoints(sleeves: T, isPreview?: boolean): Sleeve[] {
    if (sleeves.length === 0) {
      return [];
    }
    this.dispose();
    if (Array.isArray(sleeves[0])) {
      const descriptions = (sleeves as number[][][]).map((position, id) => ({
        id,
        position,
      }));
      this._sleevesMap = this.buildMap(descriptions, isPreview);
    } else {
      this._sleevesMap = this.buildMap(sleeves as SleeveDescription[], isPreview);
    }
    this._sleeves = Object.values(this._sleevesMap);
    this._map.events.add('boundschange', this.onBoundsChange);
    return this._sleeves;
  }

  public removeSleeve(sleeve: Sleeve) {
    const index = this._sleeves.findIndex((s) => s.id == sleeve.id);
    this._sleeves.splice(index, 1);
  }

  public buildSeparateSleeves(sleeves: T): Sleeve[] {
    if (sleeves.length === 0) {
      return [];
    }
    if (Array.isArray(sleeves[0])) {
      const descriptions = (sleeves as number[][][]).map((position, id) => ({
        id,
        position,
      }));
      this._sleevesMap = this.buildMap(descriptions);
    } else {
      this._sleevesMap = this.buildMap(sleeves as SleeveDescription[]);
    }
    Object.values(this._sleevesMap).forEach((sleeve) => {
      const prev = this._sleeves.findIndex((s) => s.id == sleeve.id);
      if (prev >= 0) this._sleeves[prev] = sleeve;
      else this._sleeves.push(sleeve);
    });
    return this._sleeves;
  }

  // public setSleeveColor(id: string | number, color: string) {
  //   const sleeve = this._sleevesMap[id];
  //
  //   if (!sleeve) {
  //     return;
  //   }
  //
  //   const icon = this.makeSleeveIcon(
  //     sleeve.widthRel,
  //     sleeve.heightRel,
  //     color,
  //     sleeve.strokeColor,
  //     sleeve.opacity,
  //     sleeve.isMixed,
  //   );
  //
  //   sleeve.marker.options.set(
  //     'iconImageHref',
  //     `data:image/svg+xml,${encodeURIComponent(icon)}`,
  //   );
  // }

  public buildMap(sleeves: SleeveDescription[], isPreview?: boolean) {
    const map: SleevesMap = {};
    if (!sleeves[0].position[0] || !sleeves[0].position[1]) {
      return map;
    }

    this.setPaths(
      sleeves[0].position[0],
      sleeves[0].position[1],
      this._sleeveLength,
      this._sleeveWidth,
    );

    const { width, height } = this.calculateRelativeSize();
    const fillingMaximumWeight = SLEEVE_WEIGHT_TONS_PER_METER.WHEAT * this._sleeveLength;

    for (const sleeve of sleeves) {
      const [top, bottom] = [sleeve.position[0], sleeve?.position[1]];

      if (!top || !bottom) {
        continue;
      }

      const tLocal = this.geoToLocal(top);
      const bLocal = this.geoToLocal(bottom);
      const center = this.getCenter(top, bottom);

      const angleDeg =
        (Math.atan2(bLocal[1] - tLocal[1], bLocal[0] - tLocal[0]) * 180) / Math.PI;

      const shape = this.calculateShape(width, height, this._angleLocal);
      const sleeveFillingPercentage = sleeve.weight ? (sleeve.weight / fillingMaximumWeight) * 100 : 100;

      const iconLabelRotate = Math.abs(angleDeg) > 90 ? 90 : -90;
      const icon = this.makeSleeveIconNew(
        iconLabelRotate,
        width,
        height,
        sleeve.weight,
        sleeve.color,
        sleeve.strokeColor,
        sleeve.opacity,
        false,
        0 - angleDeg - 90,
        sleeveFillingPercentage,
      );
      const mixedIcon = this.makeSleeveIcon(
        width,
        height,
        sleeve.color,
        sleeve.strokeColor,
        sleeve.opacity,
        true,
        0 - angleDeg - 90,
      );
      const addHintLayout = Array.isArray(sleeve.params) && sleeve.params.length > 0;

      const iconLabelValue = !isPreview ? sleeve.label : undefined;

      const getSleeveOptions = (isMixed?: boolean) => ({
        // @ts-ignore
        hintLayout:
          !isPreview && addHintLayout
            ? ymaps.templateLayoutFactory.createClass(`
            <table class="${sleeve.hintClassName}">
              <tbody>
                ${sleeve
                  .params!.map((p) =>
                    p.label === 'Тип'
                      ? `<tr><td>${p.label}</td><td>${
                          spreadWheatType(+p.value)?.sort +
                          ' - ' +
                          spreadWheatType(+p.value)?.type
                        }</td></tr>`
                      : `<tr><td>${p.label}</td><td>${p.value}</td></tr>`,
                  )
                  .join('')}
              </tbody>
            </table>
          `)
            : null,
        iconLayout: ymaps.templateLayoutFactory.createClass(`
            <div style="transform:rotate({{options.rotate}}deg);">
              {% include "default#image" %}
              [if options.labelValue]
                <div style="transform:translate(-50%, -50%) rotate({{ options.labelRotate }}deg);font-size:calc({{ options.imageSize[0] }}px - 1px);position:absolute;top:0;left:0;}">
                  {{ options.labelValue }}
                </div>
              [endif]
            </div>
          `),
        iconImageHref: `data:image/svg+xml,${encodeURIComponent(
          isMixed ? mixedIcon : icon,
        )}`,
        iconImageSize: [width, height],
        iconImageOffset: [-width / 2, -height / 2],
        iconRotate: angleDeg + 90,
        iconShape: sleeve.events
          ? ({
              type: 'Polygon',
              coordinates: [shape],
            } as ymaps.IGeometryJson)
          : null,
        iconLabelRotate,
        iconLabelValue,
      });

      if (!isPreview) {
        ymaps.option.presetStorage.add('sleeve#simple' + sleeve.id, getSleeveOptions());
        ymaps.option.presetStorage.add(
          'sleeve#mixed' + sleeve.id,
          getSleeveOptions(true),
        );
      }

      const options = !isPreview
        ? {
            preset:
              !isPreview && $mixedBags.getState().includes(sleeve.id.toString())
                ? 'sleeve#mixed' + sleeve.id
                : 'sleeve#simple' + sleeve.id,
          }
        : getSleeveOptions();

      const marker = new ymaps.Placemark(center, {}, options);

      map[sleeve.id] = {
        ...sleeve,
        shape,
        center,
        marker,
        angleDeg,
        widthRel: width,
        heightRel: height,
      };

      if (!isPreview && sleeve.events) {
        const events = sleeve.events;
        marker.events.add('click', (e) => events.click(map[sleeve.id], e));
        marker.events.add('contextmenu', (e) => {
          return events.context(map[sleeve.id], e);
        });
      }
    }
    return map;
  }

  private getCenter(top: number[], bottom: number[]) {
    const maxX = Math.max(top[0], bottom[0]);
    const maxY = Math.max(top[1], bottom[1]);
    const minX = Math.min(top[0], bottom[0]);
    const minY = Math.min(top[1], bottom[1]);

    return [minX + (maxX - minX) / 2, minY + (maxY - minY) / 2];
  }

  private setPaths(top: number[], bottom: number[], length: number, width: number) {
    const angleFwd = Math.atan2(bottom[1] - top[1], bottom[0] - top[0]);
    const angleRgt = angleFwd + Math.PI / 2;

    //const dirFwd = [Math.cos(angleFwd), Math.sin(angleFwd)];
    const dirRgt = [Math.cos(angleRgt), Math.sin(angleRgt)];

    const topLocal = this.geoToLocal(top);
    const botLocal = this.geoToLocal(bottom);

    this._angleLocal =
      Math.atan2(botLocal[1] - topLocal[1], botLocal[0] - topLocal[0]) + Math.PI / 2;

    //const pathH = ymaps.coordSystem.geo.solveDirectProblem(top, dirFwd, length).pathFunction;
    // @ts-ignore
    const pathW = ymaps.coordSystem.geo.solveDirectProblem(
      top,
      dirRgt,
      width,
    ).pathFunction;

    //this._pathH = pathH;
    this._pathW = pathW;

    this._top = top;
    this._bottom = bottom;
  }

  private calculateRelativeSize(zoom?: number) {
    //const p1 = this.geoToLocal(this._pathH(0).point, zoom);
    //const p2 = this.geoToLocal(this._pathH(1).point, zoom);
    const p1 = this.geoToLocal(this._top, zoom);
    const p2 = this.geoToLocal(this._bottom, zoom);
    const p3 = this.geoToLocal(this._pathW(1).point, zoom);

    const width = Math.sqrt(Math.pow(p3[0] - p1[0], 2) + Math.pow(p3[1] - p1[1], 2));
    const height = Math.sqrt(Math.pow(p2[0] - p1[0], 2) + Math.pow(p2[1] - p1[1], 2));

    return { width, height };
  }

  private geoToLocal(geoCoords: number[], zoom?: number) {
    const zFactor = zoom ?? this._map.getZoom();

    // @ts-ignore
    return this._map.converter.globalToPage(
      this._projection.toGlobalPixels(geoCoords, zFactor),
    );
  }

  private calculateShape(width: number, height: number, angle: number) {
    const dW = width / 2,
      dH = height / 2;
    const [s1, s2, s3, s4] = [
      [-dW, -dH],
      [dW, -dH],
      [dW, dH],
      [-dW, dH],
    ];

    return [
      [
        Math.cos(angle) * s1[0] - Math.sin(angle) * s1[1],
        Math.sin(angle) * s1[0] + Math.cos(angle) * s1[1],
      ],
      [
        Math.cos(angle) * s2[0] - Math.sin(angle) * s2[1],
        Math.sin(angle) * s2[0] + Math.cos(angle) * s2[1],
      ],
      [
        Math.cos(angle) * s3[0] - Math.sin(angle) * s3[1],
        Math.sin(angle) * s3[0] + Math.cos(angle) * s3[1],
      ],
      [
        Math.cos(angle) * s4[0] - Math.sin(angle) * s4[1],
        Math.sin(angle) * s4[0] + Math.cos(angle) * s4[1],
      ],
    ];
  }

  private makeSleeveIcon(
    width: number,
    height: number,
    color = 'white',
    strokeColor = '#000',
    opacity = 0.4,
    isMixed = false,
    angle: number = 0,
    fillPercentage?: number,
  ) {
    //width="${width}" height="${height}"
    return `
      <svg preserveAspectRatio="none"  viewBox="0 0 17 300" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g
           clipPath="url(#clip0_1_4)"
           id="g2">
          <path
             opacity="${opacity}"
             d="M11.1355 2.8703L16.5 53.3598L16.5 291.5C16.5 295.918 12.9183 299.5 8.5 299.5C4.08172 299.5 0.5 295.918 0.5 291.5L0.5 53.3598L5.86451 2.8703C6.00771 1.52255 7.14466 0.5 8.5 0.5C9.85534 0.5 10.9923 1.52255 11.1355 2.8703Z"
             fill="${color}"
             stroke="${strokeColor}"
             id="path1"
       />
          <!-- Head -->
          <!--          <circle cx="8.73244" cy="295.373" r="10.5641" transform="rotate(90 8.73244 295.373)" stroke="#F1898E" stroke-width="0.918616"/>-->
        </g>
        <defs
     id="defs14">
    <clipPath
       id="clip0_2010_338">
      <rect
         width="9.91211"
         height="6.07516"
         fill="white"
         transform="translate(3.80859 91.4619)"
         id="rect13" />
    </clipPath>
    <clipPath
       id="clip0_1_4">
      <rect
         width="300"
         height="17"
         fill="${color}"
         transform="matrix(0 1 -1 0 17 0)"
         id="rect14" />
    </clipPath>
    <clipPath
       id="clip0_2010_340">
      <rect
         width="9.9121103"
         height="6.07516"
         fill="#ffffff"
         transform="translate(2.80859,3.46191)"
         id="rect11"
         x="0"
         y="0" />
    </clipPath>
    <mask
       id="path-2-inside-1_2010_340"
       fill="#ffffff">
      <path
         d="M 2.80859,3.46191 H 5.36656 V 6.65937 H 2.80859 Z"
         id="path1-1" />
    </mask>
    <mask
       id="path-4-inside-2_2010_340"
       fill="#ffffff">
      <path
         d="M 5.68555,3.46191 H 7.92377 V 6.65937 H 5.68555 Z"
         id="path4-4" />
    </mask>
    <mask
       id="path-6-inside-3_2010_340"
       fill="#ffffff">
      <path
         d="M 8.24414,3.46191 H 10.8021 V 6.65937 H 8.24414 Z"
         id="path7-1" />
    </mask>
  </defs>
  <g
     id="layer1" transform="rotate(${angle} 9 291)" opacity="${isMixed ? 1 : 0}">
    <rect
       x="2.1821151"
       width="12.6667"
       height="13"
       rx="6.3333302"
       fill="#ffffff"
       id="rect1"
       y="284.0202" />
    <g
       clipPath="url(#clip0_2010_340)"
       id="g10"
       transform="translate(1.2475455,284.0202)">
      <mask
         id="mask2"
         fill="#ffffff">
        <path
           d="M 2.80859,3.46191 H 5.36656 V 6.65937 H 2.80859 Z"
           id="path2" />
      </mask>
      <path
         d="M 2.80859,3.46191 H 5.36656 V 6.65937 H 2.80859 Z"
         fill="#66b28a"
         id="path3-1" />
      <path
         d="M 5.36656,6.01988 H 2.80859 v 1.27898 h 2.55797 z"
         fill="#66b28a"
         mask="url(#path-2-inside-1_2010_340)"
         id="path5-3" />
      <mask
         id="mask6"
         fill="#ffffff">
        <path
           d="M 5.68555,3.46191 H 7.92377 V 6.65937 H 5.68555 Z"
           id="path6-4" />
      </mask>
      <path
         d="M 5.68555,3.46191 H 7.92377 V 6.65937 H 5.68555 Z"
         fill="#66b28a"
         id="path8-3" />
      <path
         d="M 7.92377,6.01988 H 5.68555 v 1.27898 h 2.23822 z"
         fill="#66b28a"
         mask="url(#path-4-inside-2_2010_340)"
         id="path9-9" />
      <mask
         id="mask10"
         fill="#ffffff">
        <path
           d="M 8.24414,3.46191 H 10.8021 V 6.65937 H 8.24414 Z"
           id="path10-9" />
      </mask>
      <path
         d="M 8.24414,3.46191 H 10.8021 V 6.65937 H 8.24414 Z"
         fill="#66b28a"
         id="path11-1" />
      <path
         d="M 10.8021,6.01988 H 8.24414 v 1.27898 h 2.55796 z"
         fill="#66b28a"
         mask="url(#path-6-inside-3_2010_340)"
         id="path12-3" />
      <circle
         cx="5.3660498"
         cy="8.5779696"
         r="0.63949102"
         fill="#66b28a"
         id="circle9" />
      <path
         d="M 3.44727,6.97949 H 10.1619 V 8.57822 H 9.20363 c -9e-5,-0.5297 -0.42952,-0.95908 -0.95924,-0.95908 -0.52972,0 -0.95915,0.42938 -0.95923,0.95908 H 6.3257 c -9e-5,-0.5297 -0.42952,-0.95908 -0.95924,-0.95908 -0.52972,0 -0.95915,0.42938 -0.95923,0.95908 H 3.44727 Z"
         fill="#66b28a"
         id="path13" />
      <circle
         cx="8.2449598"
         cy="8.5779696"
         r="0.63949102"
         fill="#66b28a"
         id="circle10" />
      <rect
         x="10.4824"
         y="7.2997999"
         width="2.55796"
         height="0.95923603"
         fill="#66b28a"
         id="rect10" />
      <line
         x1="2.8085899"
         y1="6.3394299"
         x2="10.8022"
         y2="6.3394399"
         stroke="#66b28a"
         stroke-width="0.639491"
         id="line10" />
    </g>
  </g>
      </svg>
    `;
  }

  private makeSleeveIconNew(
    iconLabelRotate: 90 | -90,
    width: number,
    height: number,
    weight?: number,
    color = 'white',
    strokeColor = '#000',
    opacity = 0.4,
    isMixed = false,
    angle: number = 0,
    fillPercentage?: number,
  ) {
    //width="${width}" height="${height}"
    return `
    <svg preserveAspectRatio="none" viewBox="0 0 17 300" fill="none" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <clipPath id="clip0_1_4">
      <path
        d="M11.1355 2.8703L16.5 53.3598L16.5 291.5C16.5 295.918 12.9183 299.5 8.5 299.5C4.08172 299.5 0.5 295.918 0.5 291.5L0.5 53.3598L5.86451 2.8703C6.00771 1.52255 7.14466 0.5 8.5 0.5C9.85534 0.5 10.9923 1.52255 11.1355 2.8703Z"
        id="main-path"
      />
    </clipPath>
  </defs>
  
  <g clip-path="url(#clip0_1_4)" opacity="${opacity}">
    <path
      opacity="0.4"
      d="M11.1355 2.8703L16.5 53.3598L16.5 291.5C16.5 295.918 12.9183 299.5 8.5 299.5C4.08172 299.5 0.5 295.918 0.5 291.5L0.5 53.3598L5.86451 2.8703C6.00771 1.52255 7.14466 0.5 8.5 0.5C9.85534 0.5 10.9923 1.52255 11.1355 2.8703Z"
      fill="white"
    />
    ${
      !weight
        ? `<rect x="0" y="0%" width="17" height="100%" fill="${color}" />`
        : `<rect x="0" y="${
          fillPercentage ? 100 - fillPercentage : 0
          }%" width="17" height="${fillPercentage}%" fill="${color}" />`
    }
    <!-- Add stroke path here -->
    <path
      d="M11.1355 2.8703L16.5 53.3598L16.5 291.5C16.5 295.918 12.9183 299.5 8.5 299.5C4.08172 299.5 0.5 295.918 0.5 291.5L0.5 53.3598L5.86451 2.8703C6.00771 1.52255 7.14466 0.5 8.5 0.5C9.85534 0.5 10.9923 1.52255 11.1355 2.8703Z"
      fill="none"
      stroke="${'none'}"
    />
  </g>
</svg>

    `;
  }

  private onBoundsChange = (e: object | ymaps.IEvent) => {
    const event = e as ymaps.Event;
    const newZoom = event.get('newZoom');
    const oldZoom = event.get('oldZoom');

    if (newZoom !== oldZoom) {
      const { width, height } = this.calculateRelativeSize(newZoom);
      const shape = this.calculateShape(width, height, this._angleLocal);

      this._sleeves.forEach((sleeve) => {
        sleeve.shape = shape;
        sleeve.widthRel = width;
        sleeve.heightRel = height;

        if (sleeve.events) {
          sleeve.marker.options.set('iconShape', {
            type: 'Polygon',
            coordinates: [shape],
          });
        }
        sleeve.marker.options.set('iconImageSize', [width, height]);
        sleeve.marker.options.set('iconImageOffset', [-width / 2, -height / 2]);
      });
    }
  };
}

export type SleeveDescription = {
  id: string | number;
  position: number[][];
  color?: string;
  strokeColor?: string;
  opacity?: number;
  events?: Events;
  hintClassName?: string;
  params?: Array<{ label: string; value: string }>;
  label?: string;
  noChange?: boolean;
  isMixed?: boolean;
  weight?: number;
};

type Events = {
  click: (target: Sleeve, e: ymaps.Event) => void;
  context: (target: Sleeve, e: ymaps.Event) => void;
};

type Options = {
  test: boolean;
};

export type Sleeve = SleeveDescription & {
  center: number[];
  angleDeg: number;
  widthRel: number;
  heightRel: number;
  shape: number[][];
  marker: ymaps.Placemark;
};

type SleevesMap = { [id: string | number]: Sleeve };
